import { useGlobalEnvironment } from '#app/shared/contexts/AppContext'
import { PagePaths } from '#app/shared/navigation/pagePaths'
import { loggerAuth0 } from '#app/shared/utils/logging'
import { type AppState, Auth0Provider, type User } from '@auth0/auth0-react'
import type React from 'react'
import { type PropsWithChildren, useCallback, useMemo } from 'react'

const PrefixSignInReturnTo = `${PagePaths.SignIn}?returnTo=`

/**
 * Extracts returnTo parameter from URL search params
 */
const getReturnToFromUrl = (): string | null => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('returnTo')
  } catch (error) {
    loggerAuth0.warn('Failed to parse URL parameters:', error)
    return null
  }
}

/**
 * Validates and sanitizes the returnTo URL to prevent open redirects
 */
const sanitizeReturnToUrl = (url: string): string => {
  try {
    // Only allow relative URLs or URLs from the same origin
    if (url.startsWith('/')) {
      return url
    }

    const urlObj = new URL(url, window.location.origin)
    if (urlObj.origin === window.location.origin) {
      return urlObj.pathname + urlObj.search + urlObj.hash
    }

    // If it's an external URL, log warning and return home
    loggerAuth0.warn(`Blocked external redirect attempt to: ${url}`)
    return PagePaths.Home
  } catch (error) {
    loggerAuth0.warn(`Invalid returnTo URL: ${url}`, error)
    return PagePaths.Home
  }
}

export const Auth0ProviderWithHistory: React.FC<PropsWithChildren> = ({
  children
}) => {
  const env = useGlobalEnvironment()

  // Memoize auth configuration to prevent unnecessary re-renders
  const authConfig = useMemo(() => {
    if (!env.AUTH0_DOMAIN || !env.AUTH0_CLIENT_ID) {
      throw new Error(
        'Auth0 configuration is missing. Please check AUTH0_DOMAIN and AUTH0_CLIENT_ID environment variables.'
      )
    }

    return {
      domain: env.AUTH0_DOMAIN,
      clientId: env.AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: `${window.location.origin}${PagePaths.Home}`,
        audience: env.API_IDENTIFIER
      }
    }
  }, [env.AUTH0_DOMAIN, env.AUTH0_CLIENT_ID, env.API_IDENTIFIER])

  const onRedirectCallback = useCallback((appState?: AppState, user?: User) => {
    let returnTo = appState?.returnTo

    // Clean up the returnTo URL if it has the sign-in prefix
    if (returnTo?.startsWith(PrefixSignInReturnTo)) {
      returnTo = returnTo.replace(PrefixSignInReturnTo, '')
    }

    // If no returnTo in appState, check URL params
    if (!returnTo) {
      returnTo = getReturnToFromUrl() ?? undefined
    }

    // Sanitize and validate the return URL
    const redirectTo = returnTo
      ? sanitizeReturnToUrl(returnTo)
      : PagePaths.Home

    loggerAuth0.debug('Auth0 redirect callback', {
      appStateReturnTo: appState?.returnTo,
      urlReturnTo: getReturnToFromUrl(),
      finalRedirectTo: redirectTo,
      userId: user?.sub,
      currentUrl: window.location.href
    })

    // Let React Router handle navigation naturally instead of forcing window.location.href
    // The Auth0Provider will clean up the URL parameters and the app state will update
    // Only navigate if we need to go somewhere other than current location
    if (redirectTo !== window.location.pathname) {
      window.history.replaceState({}, '', redirectTo)
    }
  }, [])

  return (
    <Auth0Provider
      {...authConfig}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  )
}

export default Auth0ProviderWithHistory
